import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Column } from "primereact/column";
import { Text } from "../../components/Text";
import { CustomButton } from "../../components/CustomButton";
import { ReactComponent as Edit } from "../../svg/edit.svg";
import { ReactComponent as Delete } from "../../svg/delete.svg";
import { DeleteAlert } from "../../components/Alert/DeleteAlert";
import { useDispatch, useSelector } from "react-redux";
import {
  changeSelectedCategory,
  getCategories,
  changePage,
  changelimit,
  resetCategoryList,
  changeSelectedCategories,
  bulkDeleteCategory,
  reseSelectedCategories,
  updateCategory,
  addCategory,
} from "../../reducers/categoryTableSlice";
import CustomPaginator from "../../components/CustomPaginator";
import DebounceSearch from "../../components/common/DebounceSearch";
import TableActionsContainer from "../../components/CustomTable/TableActionsContainer";
import { useLayout } from "../../context/LayoutContext";
import SklbtConfirmation from "../../components/common/SklbtConfirmation";
import modulesName from "../../roles/modulesName";
import permissions from "../../roles/permissions";
import { InputSwitch } from "primereact/inputswitch";
import { Tag } from "primereact/tag";

const CategoryList = () => {
  const [displayAlertDelete, setDisplayAlertDelete] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [bulkDeleteAlert, setBulkDeleteAlert] = useState(false);
  const { toast, setLoading } = useLayout(null);
  const dispatch = useDispatch();

  const {
    categoryData,
    page,
    limit,
    loading,
    totalCategoryCount,
    selectedCategories,
  } = useSelector((state) => state.categoryTable);

  const { accesskey } = useSelector((state) => state.authenticate);

  // Inline edit/add states
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [newCategory, setNewCategory] = useState({
    categoryName: "",
    status: "Available",
  });

  // Filter states
  const [filtersData, setFiltersData] = useState({
    status: "",
  });

  const [filters1, setFilters1] = useState({
    status: { value: "" },
  });

  const statuses = ["Available", "Unavailable"];

  useEffect(() => {
    return () => {
      dispatch(resetCategoryList());
      dispatch(reseSelectedCategories());
    };
  }, []);

  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  // Inline edit handlers
  const handleEdit = (category) => {
    setEditingRow(category.id);
    setEditData({ ...category });
  };

  const handleChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = (id) => {
    dispatch(updateCategory({ categoryId: id, data: editData }))
      .unwrap()
      .then(() => {
        toast?.current?.show({
          severity: "success",
          detail: "Category updated successfully",
        });
        setEditingRow(null);
        dispatch(getCategories({ page, limit, globalFilterValue: inputValue }));
      })
      .catch((err) => {
        toast?.current?.show({
          severity: "error",
          detail: err?.message || "Failed to update category",
        });
      });
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditData({});
  };

  // Add category handlers
  const handleAddCategory = () => {
    setIsAdding(true);
    setNewCategory({ categoryName: "", status: "Available" });
  };

  const handleSaveNew = () => {
    dispatch(addCategory(newCategory))
      .unwrap()
      .then(() => {
        toast?.current?.show({
          severity: "success",
          detail: "Category added successfully",
        });
        setIsAdding(false);
        setNewCategory({ categoryName: "", status: "Available" });
        dispatch(getCategories({ page, limit, globalFilterValue: inputValue }));
      })
      .catch((err) => {
        toast?.current?.show({
          severity: "error",
          detail: err?.message || "Failed to add category",
        });
      });
  };

  const handleCancelNew = () => {
    setIsAdding(false);
    setNewCategory({ categoryName: "", status: "Available" });
  };

  // Delete handlers
  const handleDelete = (category) => {
    dispatch(changeSelectedCategory(category));
    setDisplayAlertDelete(true);
  };

  const deleteModule = () => {
    return (
      <DeleteAlert
        item="category"
        displayAlertDelete={displayAlertDelete}
        setDisplayAlertDelete={setDisplayAlertDelete}
        toast={toast}
      />
    );
  };

  // Bulk delete handlers
  function handleSelectCategory(selectedData) {
    dispatch(changeSelectedCategories({ selectedData, categoryData }));
  }

  const handleBulkDelete = () => {
    const categoryIds = selectedCategories?.map((category) => category?.id);
    dispatch(bulkDeleteCategory(categoryIds))
      .unwrap()
      .then((res) => {
        setBulkDeleteAlert(false);
        toast?.current?.show({
          severity: "success",
          detail: `${res.length} Categories deleted successfully`,
          life: 3000,
        });
        dispatch(getCategories({ page, limit, globalFilterValue: inputValue }));
      })
      .catch((err) => {
        toast?.current?.show({
          severity: "error",
          detail: err?.error || err?.message,
          life: 3000,
        });
      });
  };

  // Filter handlers
  const statusFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={statuses}
        onChange={(e) => {
          options.filterCallback(e.value, options.index);
        }}
        placeholder="Select Status"
        className="p-column-filter"
      />
    );
  };

  const onClickFilter = (e) => {
    if (e.field === "status") {
      setFilters1((prev) => ({
        ...prev,
        status: { ...prev.status, value: e.constraints.value },
      }));
      setFiltersData((prev) => ({
        ...prev,
        status: e.constraints.value,
      }));
    }
  };

  const onClearStatusFilter = () => {
    setFilters1((prev) => ({
      ...prev,
      status: { ...prev.status, value: "" },
    }));
    setFiltersData((prev) => ({
      ...prev,
      status: "",
    }));
  };

  const managePaginationData = () => {
    let filterData = [];
    if (filtersData.status) {
      filterData.push({
        key: "status",
        value: filtersData.status,
      });
    }
    return {
      page,
      limit,
      filterData,
      globalFilterValue: inputValue,
    };
  };

  // Inline editable columns
  const nameBodyTemplate = (rowData) => {
    if (editingRow === rowData.id) {
      return (
        <input
          type="text"
          value={editData.categoryName || ""}
          onChange={(e) => handleChange("categoryName", e.target.value)}
          className="p-inputtext p-component w-full"
        />
      );
    }
    return rowData.categoryName;
  };

  const statusBodyTemplate = (rowData) => {
    if (editingRow === rowData.id) {
      return (
        <InputSwitch
          className={editData.status === "Available" ? "custom-enabled" : ""}
          checked={editData.status === "Available"}
          onChange={(e) =>
            handleChange("status", e.value ? "Available" : "Unavailable")
          }
        />
      );
    }
    return (
      <Tag
        value={rowData.status}
        severity={rowData.status === "Available" ? "success" : "danger"}
        rounded
      />
    );
  };

  // Actions template
  const actionBodyTemplate = (rowData) => {
    if (editingRow === rowData.id) {
      return (
        <div className="flex gap-2">
          <CustomButton
            label="Save"
            varient="filled"
            onClick={() => handleSave(rowData.id)}
          />
          <CustomButton
            label="Cancel"
            varient="cancel"
            onClick={handleCancel}
          />
        </div>
      );
    }
    return (
      <div className="flex gap-2">
        {accesskey[modulesName.CATEGORY]?.includes(permissions.UPDATE) && (
          <CustomButton
            varient="icon-button"
            onClick={() => handleEdit(rowData)}
            icon={<Edit />}
          />
        )}
        {accesskey[modulesName.CATEGORY]?.includes(permissions.DELETE) && (
          <CustomButton
            varient="icon-button"
            onClick={() => handleDelete(rowData)}
            icon={<Delete />}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    dispatch(getCategories(managePaginationData()))
      .unwrap()
      .catch(() => {});
  }, [page, limit, inputValue, filtersData]);

  return (
    <div className="p-3 ">
      <style>{`
        .custom-enabled {
          background-color: #35e277ff !important;
          border-radius: 20px;
        }
        .custom-enabled .p-inputswitch-slider {
          background-color: var(--switch-enabled-bg, #35e277ff) !important;
        }
      `}</style>

      {bulkDeleteAlert && (
        <SklbtConfirmation
          visible={bulkDeleteAlert}
          handleConfirm={handleBulkDelete}
          handleReject={() => setBulkDeleteAlert(false)}
          message={`Are you sure, you want to delete ${selectedCategories.length} categories?`}
        />
      )}
      <TableActionsContainer>
        <div className="flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="flex justify-content-between align-items-center gap-2 flex-grow-1">
            <div className="flex align-items-center gap-2">
              <Text type={"heading"}>Category List</Text>
              {selectedCategories.length !== 0 && (
                <CustomButton
                  varient="icon-button"
                  label={
                    <Text>({selectedCategories.length}) Delete Selected</Text>
                  }
                  onClick={() => setBulkDeleteAlert(true)}
                />
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <CustomButton
              label="Add Category"
              varient="filled"
              onClick={handleAddCategory}
            />
            <DebounceSearch
              placeholder={"Search Category"}
              inputValue={inputValue}
              handleInputChange={(value) => setInputValue(value)}
            />
          </div>
        </div>
      </TableActionsContainer>
      <div className="card my-3">
        {deleteModule()}
        <div className="card p-fluid">
          <DataTable
            value={isAdding ? [newCategory, ...categoryData] : categoryData}
            dataKey="id"
            responsiveLayout="scroll"
            resizableColumns
            columnResizeMode="expand"
            className="skalebot-table"
            selection={selectedCategories}
            onSelectionChange={(e) => handleSelectCategory(e.value)}
            selectionMode={
              accesskey[modulesName.CATEGORY]?.includes(permissions.DELETE)
                ? "checkbox"
                : ""
            }
            filters={filters1}
            filterDisplay="menu"
            globalFilterFields={["categoryName"]}
          >
            {accesskey[modulesName.CATEGORY]?.includes(permissions.DELETE) && (
              <Column
                selectionMode="multiple"
                headerStyle={{ width: "3rem" }}
                exportable={false}
              />
            )}
            <Column
              field="categoryName"
              header="Category Name"
              body={(rowData) => {
                if (isAdding && rowData === newCategory) {
                  return (
                    <input
                      type="text"
                      value={newCategory.categoryName}
                      onChange={(e) =>
                        setNewCategory((prev) => ({
                          ...prev,
                          categoryName: e.target.value,
                        }))
                      }
                      className="p-inputtext p-component w-full"
                    />
                  );
                }
                return nameBodyTemplate(rowData);
              }}
            />
            <Column
              field="status"
              header="Status"
              body={(rowData) => {
                if (isAdding && rowData === newCategory) {
                  return (
                    <InputSwitch
                      className={
                        newCategory.status === "Available"
                          ? "custom-enabled"
                          : ""
                      }
                      checked={newCategory.status === "Available"}
                      onChange={(e) =>
                        setNewCategory((prev) => ({
                          ...prev,
                          status: e.value ? "Available" : "Unavailable",
                        }))
                      }
                    />
                  );
                }
                return statusBodyTemplate(rowData);
              }}
              style={{ maxWidth: "180px" }}
              filter
              showFilterMatchModes={false}
              filterElement={statusFilterTemplate}
              onFilterApplyClick={(e) => onClickFilter(e)}
              onFilterClear={onClearStatusFilter}
            />
            <Column
              header="Actions"
              body={(rowData) => {
                if (isAdding && rowData === newCategory) {
                  return (
                    <div className="flex gap-2">
                      <CustomButton
                        label="Save"
                        varient="filled"
                        onClick={handleSaveNew}
                      />
                      <CustomButton
                        label="Cancel"
                        varient="cancel"
                        onClick={handleCancelNew}
                      />
                    </div>
                  );
                }
                return actionBodyTemplate(rowData);
              }}
              style={{ width: "180px" }}
            />
          </DataTable>
        </div>
        <div className="mt-3">
          <TableActionsContainer>
            <div className="flex justify-content-end">
              <CustomPaginator
                page={page}
                limit={limit}
                changelimit={changelimit}
                totalRecords={totalCategoryCount}
                changePage={changePage}
              />
            </div>
          </TableActionsContainer>
        </div>
      </div>
    </div>
  );
};

export default CategoryList;
